(ns nl.mediquest.ui.core.section
  (:require
   [clojure.spec.alpha :as s]
   [integrant.core :as ig]
   [nl.mediquest.ui.component :as component]
   [nl.mediquest.ui.sticky-event :refer [sticky]]
   [reagent.core :as r]))

(s/def :section/title string?)
(s/def :section/id string?)
(s/def :section/width (s/or :string string? :keyword keyword?))
(s/def :section/color keyword?)
(s/def :section/icon keyword?)
(s/def :section/icon-weight #{:fa :fal :fas :far :fad})
(s/def :section/description any?)

(defn- render-title [icon icon-weight title]
  [:div.text-xl.font-semibold
   (when icon
     [:i.pr-2 {:class [icon icon-weight]}])
   title])

(defn- render-description [description]
  [:div.text-gray-800
   (cond
     (and (vector? description)
          (string? (first description)))
     (apply str description)
     (some? description)
     [:div description])])

(defn- render-components [components context params]
  [:div.flex.flex-wrap.justify-center.sm:justify-start
   (component/render-components components context params)])

(defn- empty-components? [components]
  (and (coll? components)
       (empty? components)))

(defn- page-break-before? [class]
  (contains? (set class) "page-break-before"))

(defn- add-data-pdf
  [attributes {:section/keys [class title description] :as _opts}]
  (assoc attributes
         :data-pdf (pr-str {:title title
                            :description description
                            :type :text
                            :page-break-before?
                            (page-break-before? class)
                            :font-size 12
                            :margin-top 2
                            :margin-bottom 0.5})))

(defn- print-hidden? [class]
  (contains? (set class) "print:hidden"))

(defn- pdf-element? [{:section/keys [class title description] :as _opts}]
  (and (or (seq title) (seq description))
       (not (print-hidden? class))))

(defn- add-class-pdf [attributes]
  (update attributes :class #((fnil conj []) % "pdf")))

(defn- add-pdf-attributes
  [attributes opts]
  (-> attributes
      add-class-pdf
      (add-data-pdf opts)))

(defn- view-args [{:section/keys [class color components bg width collapsible?]
                   :or {color "text-primary"
                        bg "bg-none"
                        class []}
                   :as opts}]
  (cond-> {:class
           (-> class
               (into [color bg (or width "w-full")
                      (when (empty-components? components) "hidden")])
               (into (if collapsible? ["pl-2" "-mt-2"] ["p-2" "pt-4"])))}
    (pdf-element? opts)
    (add-pdf-attributes opts)))


(defn- collapsable-view
  "Collapsable section.
  Requires DaisyUI plugin in TailwindCSS in app using mq-ui.
  See portaal's tailwind.config.js for an example."
  [{:section/keys [title icon icon-weight description components]
    :or {icon-weight :fa}
    :as _opts}
   context params]
  (let [collapsed? (r/atom true)]
    (fn []
      [:div.mx-3.collapse {:tab-index "0"}
       ;; Due to DaisyUI magic the checkbox ensures more than one collapsible
       ;; can be opened at a time (no accordeon effect). Also the onclick
       ;; handler and a pointer cursor are available on the collapse title.
       [:input {:type :checkbox :on-click #(swap! collapsed? not)}]
       [:div.collapse-title
        [:div.flex
         [:label.fas.pr-4.pt-1
          {:class (if @collapsed? :fa-chevron-down :fa-chevron-up)}]
         [render-title icon icon-weight title]]
        [render-description description]]
       [:div.collapse-content
        [render-components components context params]]])))

(defn- default-view
  [{:section/keys [id title icon icon-weight description components]
    :or {icon-weight :fa}
    :as _opts}
   context params]
  [:div.mx-3
   (when id {:id id})
   [render-title icon icon-weight title]
   [render-description description]
   [render-components components context params]])

(def ^:private sticky-classes
  ["top-0" "z-20" "[&[stuck]]:bg-white" "[&[stuck]]:rounded-b"
   "[&[stuck]]:border-x" "[&[stuck]]:border-b" "[&[stuck]]:border-gray-300"
   "[&[stuck]]:drop-shadow-md" "peer/sticky"])

(defn- body [{:section/keys [collapsible?] :as opts} context params]
  [:div
   (view-args opts)
   (if collapsible?
     [collapsable-view opts context params]
     [default-view opts context params])])

(defn view [{:section/keys [sticky?] :as opts} context params]
  (if sticky?
    [sticky {:class sticky-classes}
     [body opts context params]]
    [body opts context params]))

(defmethod ig/assert-key :component.ui/section [_k v]
  (assert (s/valid? (s/keys :req [:section/title]
                            :opt [:section/id
                                  :section/icon
                                  :section/color
                                  :section/description])
                    v)))

(defmethod ig/init-key :component.ui/section [_ opts]
  (fn [context params]
    [view opts context params]))
